import { useState } from "react";
import { Share2, Check } from "lucide-react";

function copyToClipboard(text) {
  // Fallback approach that works in iframes and all contexts
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "-9999px";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand("copy");
  } catch (e) {
    // silent fail
  }
  document.body.removeChild(textarea);
}

function openShareUrl(url) {
  // Use an anchor element to guarantee a new tab even inside iframes
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function SocialShareButtons({ shareText, pageUrl }) {
  const [copied, setCopied] = useState(null); // "instagram" | "tiktok" | "link" | null

  const url = pageUrl || window.location.href;

  const handleCopy = (platform, message) => {
    copyToClipboard(url);
    setCopied(platform);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="mt-6 flex items-center justify-center gap-3 flex-wrap" data-testid="social-share-buttons">
      <span className="text-sm text-slate-500 flex items-center gap-1">
        <Share2 className="w-4 h-4" /> Share:
      </span>

      {/* Facebook */}
      <button
        onClick={() => openShareUrl(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)}
        className="w-9 h-9 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
        data-testid="share-facebook"
        aria-label="Share on Facebook"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      </button>

      {/* Instagram */}
      <button
        onClick={() => handleCopy("instagram")}
        className="relative w-9 h-9 rounded-full text-white flex items-center justify-center hover:opacity-80 transition-opacity"
        style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}
        data-testid="share-instagram"
        aria-label="Share on Instagram"
      >
        {copied === "instagram" ? (
          <Check className="w-4 h-4" />
        ) : (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
        )}
      </button>
      {copied === "instagram" && (
        <span className="absolute mt-12 text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded">Link copied for Instagram!</span>
      )}

      {/* X (Twitter) */}
      <button
        onClick={() => openShareUrl(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText || "")}&url=${encodeURIComponent(url)}`)}
        className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center hover:opacity-80 transition-opacity"
        data-testid="share-twitter"
        aria-label="Share on X"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      </button>

      {/* TikTok */}
      <button
        onClick={() => handleCopy("tiktok")}
        className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center hover:opacity-80 transition-opacity"
        data-testid="share-tiktok"
        aria-label="Share on TikTok"
      >
        {copied === "tiktok" ? (
          <Check className="w-4 h-4" />
        ) : (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.48V12.8a8.28 8.28 0 005.58 2.16V11.5a4.84 4.84 0 01-3.59-1.58V6.69h3.59z"/></svg>
        )}
      </button>

      {/* Copy Link */}
      <button
        onClick={() => handleCopy("link")}
        className="w-9 h-9 rounded-full bg-[#7C2C3B] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
        data-testid="share-link"
        aria-label="Copy link"
      >
        {copied === "link" ? (
          <Check className="w-4 h-4" />
        ) : (
          <Share2 className="w-4 h-4" />
        )}
      </button>

      {/* Copied feedback toast */}
      {copied && (
        <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full border border-green-200 animate-pulse">
          {copied === "instagram" ? "Copied for Instagram!" : copied === "tiktok" ? "Copied for TikTok!" : "Link copied!"}
        </span>
      )}
    </div>
  );
}
